import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import {
  completeZohoOAuthCallback,
  createInventoryConnection,
  listInventoryConnections,
  startInventoryConnect,
} from '../inventory/inventoryApi'
import { clearZohoOAuthCallback, readZohoOAuthCallbackParams, type ZohoOAuthCallbackParams } from '../inventory/zohoOAuthSession'
import { Button } from '../components/ui/Button'
import { Icon, I } from '../components/ui/icons'

type Phase = 'submitting' | 'success' | 'error'

interface ZohoOAuthPageProps {
  accessToken: string
  onDone: () => void
}

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.zohoOAuth.errors.codes.${error.code}`
  }
  return 'pages.zohoOAuth.errors.codes.REQUEST_FAILED'
}

export function ZohoOAuthPage({ accessToken, onDone }: ZohoOAuthPageProps) {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<Phase>('submitting')
  const [error, setError] = useState<unknown>(null)
  const params = useMemo<ZohoOAuthCallbackParams>(() => readZohoOAuthCallbackParams(), [])
  const sentRef = useRef(false)

  useEffect(() => {
    if (sentRef.current) return
    sentRef.current = true

    if (typeof window !== 'undefined' && (params.code || params.state || params.error || params.errorDescription)) {
      window.history.replaceState({}, '', '/zoho-oauth')
    }

    if (params.error) {
      Promise.resolve().then(() => {
        setPhase('error')
        setError(new ApiClientError(400, {
          code: 'INVENTORY_OAUTH_CALLBACK_FAILED',
          message: params.errorDescription ?? params.error ?? undefined,
        }))
      })
      return
    }

    if (!params.state) {
      listInventoryConnections(accessToken)
        .then(async connections => {
          const existing = connections.find(connection => connection.provider === 'ZOHO')
          const connection = existing ?? await createInventoryConnection(accessToken, {
            provider: 'ZOHO',
            displayName: 'Zoho Inventory',
          })
          const start = await startInventoryConnect(accessToken, connection.id)
          if (start.authorizationUrl && typeof window !== 'undefined') {
            clearZohoOAuthCallback()
            window.location.assign(start.authorizationUrl)
            return
          }
          throw new ApiClientError(502, { code: 'INVENTORY_CONNECT_FAILED' })
        })
        .catch(caught => {
          setPhase('error')
          setError(caught)
        })
      return
    }

    if (!params.code) {
      Promise.resolve().then(() => {
        setPhase('error')
        setError(new ApiClientError(400, { code: 'INVENTORY_OAUTH_CALLBACK_FAILED' }))
      })
      return
    }

    completeZohoOAuthCallback(accessToken, {
      code: params.code,
      state: params.state,
      error: params.error,
      errorDescription: params.errorDescription,
    })
      .then(() => {
        setPhase('success')
        clearZohoOAuthCallback()
      })
      .catch(caught => {
        setPhase('error')
        setError(caught)
      })
  }, [accessToken, params])

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 28px' }}>
        {phase === 'submitting' && (
          <div style={{ width: 520, background: 'white', borderRadius: 16, border: '1px solid var(--border-subtle)', padding: 36, textAlign: 'center', boxShadow: '0 12px 40px rgba(15,23,42,0.06)' }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--brand-100)', color: 'var(--brand-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <Icon size={26}>{I.refresh}</Icon>
            </span>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.015em' }}>
              {t('pages.zohoOAuth.submitting.title')}
            </div>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 8, lineHeight: 1.55 }}>
              {t('pages.zohoOAuth.submitting.body')}
            </p>
          </div>
        )}

        {phase === 'success' && (
          <div style={{ width: 520, background: 'white', borderRadius: 16, border: '1px solid var(--border-subtle)', padding: 36, textAlign: 'center', boxShadow: '0 12px 40px rgba(15,23,42,0.06)' }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--success-100)', color: 'var(--success)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <Icon size={26} stroke={3}>{I.check}</Icon>
            </span>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.015em' }}>
              {t('pages.zohoOAuth.success.title')}
            </div>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 8, lineHeight: 1.55 }}>
              {t('pages.zohoOAuth.success.body')}
            </p>
            <Button kind="primary" size="md" icon={I.arrowR} style={{ marginTop: 22 }} onClick={onDone}>
              {t('pages.zohoOAuth.success.back')}
            </Button>
          </div>
        )}

        {phase === 'error' && (
          <div style={{ width: 480, background: 'white', borderRadius: 16, border: '1px solid var(--border-subtle)', padding: 36, textAlign: 'center', boxShadow: '0 12px 40px rgba(15,23,42,0.06)' }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--danger-100)', color: 'var(--danger)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <Icon size={26} stroke={3}>{I.alert}</Icon>
            </span>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.015em' }}>
              {t('pages.zohoOAuth.error.title')}
            </div>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 8, lineHeight: 1.55 }}>
              {String(t(errorMessageKey(error), { defaultValue: String(t('pages.zohoOAuth.errors.codes.REQUEST_FAILED')) }))}
            </p>
            <Button kind="secondary" size="md" icon={I.arrowR} style={{ marginTop: 22 }} onClick={onDone}>
              {t('pages.zohoOAuth.error.back')}
            </Button>
          </div>
        )}
      </div>

      <div style={{ padding: '16px 28px', borderTop: '1px solid var(--border-subtle)', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12, color: 'var(--fg-subtle)' }}>
          <Icon size={14} color="var(--fg-subtle)">{I.shield}</Icon>
          {t('pages.zohoOAuth.footer.security')}
        </div>
      </div>
    </div>
  )
}
